import { useEffect, useState } from 'react';
import { authDebugLog, clearPostAuthRedirect, clearStoredLoginAuthError, clearStoredLoginSuccessMessage, exchangeCodeForSessionFromUrl, getPostAuthRedirect, getStoredAppIdentity, redeemBetaInvitation, redeemGenericPartnerInvitation, redeemWarriorPartnerInvitation, sanitizeLoginAuthErrorMessage, setStoredLoginAuthError, setStoredLoginSuccessMessage, signOut } from '../../lib/auth.ts';

export default function AuthCallbackPage() {
  const [message, setMessage] = useState('Completing sign-in...');
  const [error, setError] = useState<string | null>(null);

  const navigateTo = (nextPath: string) => {
    try {
      window.history.replaceState({}, '', nextPath);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    } catch {
      try {
        window.location.href = nextPath;
      } catch {}
    }
  };

  useEffect(() => {
    let cancelled = false;
    let redirectTimer: number | null = null;

    (async () => {
      try {
        setMessage('Completing sign-in...');
        const url = new URL(window.location.href);
        const isEmailConfirmationFlow = String(url.searchParams.get('flow') || '').trim() === 'email-confirmation';
        const isBetaConfirmationFlow = String(url.searchParams.get('flow') || '').trim() === 'beta-confirmation';
        const isWarriorPartnerFlow = String(url.searchParams.get('flow') || '').trim() === 'warrior-partner-access';
        const isGenericPartnerFlow = String(url.searchParams.get('flow') || '').trim() === 'generic-partner-access';
        authDebugLog('callback_enter', {
          isEmailConfirmationFlow,
          isBetaConfirmationFlow,
          isWarriorPartnerFlow,
          isGenericPartnerFlow,
          hasCode: url.searchParams.has('code'),
          codePrefix: url.searchParams.get('code') ? String(url.searchParams.get('code')).slice(0, 8) : null,
        });
        const session = await exchangeCodeForSessionFromUrl();
        if (cancelled) return;
        authDebugLog('callback_exchange_result', {
          hasSession: !!session,
          userId: session?.user?.id || null,
          userEmail: session?.user?.email || null,
          isEmailConfirmationFlow,
          isBetaConfirmationFlow,
          isWarriorPartnerFlow,
          isGenericPartnerFlow,
        });

        if (isBetaConfirmationFlow) {
          const invitationToken = String(url.searchParams.get('invite') || '').trim();
          if (!invitationToken) throw new Error('The beta invitation link is missing its invitation token.');
          if (!session) throw new Error('Email confirmation did not return an authenticated session. Please sign in to activate your invitation.');
          await redeemBetaInvitation(invitationToken);
          if (cancelled) return;
          clearStoredLoginAuthError();
          clearPostAuthRedirect();
          clearStoredLoginSuccessMessage();
          navigateTo('/dashboard');
          return;
        }

        if (isWarriorPartnerFlow) {
          const invitationToken = String(url.searchParams.get('invite') || '').trim();
          if (!invitationToken) throw new Error('The partner invitation is missing its access token.');
          if (!session?.access_token) throw new Error('The invitation did not return an authenticated session. Please reopen your invitation.');
          await redeemWarriorPartnerInvitation(invitationToken, session.access_token);
          if (cancelled) return;
          clearStoredLoginAuthError();
          clearPostAuthRedirect();
          clearStoredLoginSuccessMessage();
          navigateTo('/dashboard');
          return;
        }

        if (isGenericPartnerFlow) {
          const invitationToken = String(url.searchParams.get('invite') || '').trim();
          if (!invitationToken) throw new Error('The partnership invitation is missing its access token.');
          if (!session?.access_token) throw new Error('The invitation did not return an authenticated session. Please reopen your invitation.');
          await redeemGenericPartnerInvitation(invitationToken, session.access_token);
          if (cancelled) return;
          clearStoredLoginAuthError();
          clearPostAuthRedirect();
          clearStoredLoginSuccessMessage();
          navigateTo('/dashboard');
          return;
        }

        if (isEmailConfirmationFlow) {
          clearStoredLoginAuthError();
          clearPostAuthRedirect();
          clearStoredLoginSuccessMessage();
          await signOut({ scope: 'local' }).catch(() => undefined);
          setStoredLoginSuccessMessage('Email confirmed. You can now sign in.');
          authDebugLog('callback_redirect_login', {
            reason: 'email_confirmation_complete',
            target: '/login',
          });
          navigateTo('/login');
          return;
        }

        const identity = session ? session.user : getStoredAppIdentity();
        if (identity) {
          clearStoredLoginAuthError();
          const redirectTo = getPostAuthRedirect();
          clearPostAuthRedirect();
          const userId = 'uid' in identity ? identity.uid : identity.id;
          const userEmail = identity.email ?? null;
          authDebugLog('callback_redirect_success', {
            redirectTo,
            hasSession: !!session,
            identityFromStorage: !session,
            userId,
            userEmail,
          });
          navigateTo(redirectTo.startsWith('/') ? redirectTo : '/');
          return;
        }

        throw new Error('No authenticated session was returned.');
      } catch (err) {
        if (cancelled) return;
        const messageText = err instanceof Error ? err.message : 'Authentication failed.';
        const safeMessage = sanitizeLoginAuthErrorMessage(messageText);
        authDebugLog('callback_redirect_login', {
          reason: 'exchange_failed_or_missing_session',
          message: messageText,
          safeMessage,
        });
        setStoredLoginAuthError(messageText);
        setError(safeMessage);
        setMessage('Returning to sign in...');
        redirectTimer = window.setTimeout(() => {
          navigateTo('/login');
        }, 1200) as unknown as number;
      }
    })();

    return () => {
      cancelled = true;
      if (redirectTimer) window.clearTimeout(redirectTimer);
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] p-6 text-slate-200">
      <div className="w-full max-w-sm rounded-2xl border border-slate-700/50 bg-slate-900/85 px-6 py-5 text-center shadow-2xl backdrop-blur-xl">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
        <div className="mt-4 text-sm font-semibold">{message}</div>
        {error ? <div className="mt-2 text-sm text-slate-400">{error}</div> : null}
      </div>
    </div>
  );
}
